import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import * as E from '@react-email/components'
import { data, redirect, Form } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
	findRedeemableInviteCode,
	pendingInviteCodeIdKey,
} from '#app/utils/invite-code.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import { type Route } from './+types/signup.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const SignupSchema = z.object({
	email: EmailSchema,
	inviteCode: z
		.string({ required_error: "Code d'invitation requis" })
		.min(1, "Code d'invitation requis"),
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return null
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()

	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'Un utilisateur existe déjà avec cet e-mail',
				})
				return
			}
			const invite = await findRedeemableInviteCode(data.inviteCode)
			if (!invite) {
				ctx.addIssue({
					path: ['inviteCode'],
					code: z.ZodIssueCode.custom,
					message: "Code d'invitation invalide ou expiré",
				})
				return
			}
		}).transform(async (data) => {
			const invite = await findRedeemableInviteCode(data.inviteCode)
			return { ...data, inviteId: invite?.id ?? null }
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { email, inviteId } = submission.value
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: `Bienvenue sur Le Monastère !`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		const verifySession = await verifySessionStorage.getSession(
			request.headers.get('cookie'),
		)
		verifySession.set(pendingInviteCodeIdKey, inviteId)
		return redirect(redirectTo.toString(), {
			headers: {
				'set-cookie': await verifySessionStorage.commitSession(verifySession),
			},
		})
	} else {
		return data(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang='fr' dir='ltr'>
			<E.Container>
				<h1>
					<E.Text>Bienvenue sur Le Monastère !</E.Text>
				</h1>
				<p>
					<E.Text>
						Voici votre code de vérification : <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Ou cliquez sur le lien pour commencer :</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Inscription | Le Monastère' }]
}

export default function SignupRoute({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className='container flex flex-col justify-center pt-20 pb-32'>
			<div className='text-center'>
				<h1 className='text-h1'>Commençons l'aventure !</h1>
				<p className='text-body-md text-muted-foreground mt-3'>
					Veuillez entrer votre e-mail.
				</p>
			</div>
			<div className='mx-auto mt-16 max-w-sm min-w-full sm:min-w-[368px]'>
				<Form className='space-y-4' method='POST' {...getFormProps(form)}>
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.email.id,
							children: 'E-mail',
						}}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
							autoFocus: true,
							autoComplete: 'email',
						}}
						errors={fields.email.errors}
					/>
					<Field
						labelProps={{
							htmlFor: fields.inviteCode.id,
							children: "Code d'invitation",
						}}
						inputProps={{
							...getInputProps(fields.inviteCode, { type: 'text' }),
							autoComplete: 'off',
							autoCapitalize: 'characters',
							className: 'uppercase',
						}}
						errors={fields.inviteCode.errors}
					/>
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className='w-full'
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type='submit'
						disabled={isPending}
					>
						Soumettre
					</StatusButton>
				</Form>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
